"use client";

import { iGenericAccessDetail } from "@/app/models/interfaceProject";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import InputChat from "../../Elements/Core/InputChat";
import { Action } from "@/app/constants/enums";

interface Props {
  config: iGenericAccessDetail;
  onSubmit: (value: string) => void;
  loading?: boolean;
}

export default function DynamicFormField({
  config,
  onSubmit,
  loading = false,
}: Props) {
  const [value, setValue] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = `listbox-${config.label.replace(/\s+/g, "-")}`;

  /**
   * Detecta si es lista
   */
  const { inputType, inputAttrs, listValues } = useMemo(() => {
    const attrs: Record<string, any> = {};
    let derivedType = "text";
    let values: string[] | null = null;

    for (const rule of config.validation) {
      switch (rule.tipo) {
        case "required":
          attrs.required = true;
          break;
        case "email":
          derivedType = "email";
          break;
        case "numeric":
          derivedType = "number";
          attrs.inputMode = "numeric";
          break;
        case "list":
          values = rule.permissions_list ?? [];
          break;
      }
    }

    return { inputType: derivedType, inputAttrs: attrs, listValues: values };
  }, [config.validation]);

  /**
   * 🔹 FILTRADO SEGURO (NO CRASHEA)
   */
  const filtered = useMemo(() => {
    const safeValue = value ?? "";

    if (!listValues || !safeValue.trim()) return [];

    return listValues
      .filter((v) =>
        v.toLowerCase().includes(safeValue.toLowerCase())
      )
      .slice(0, 5);
  }, [listValues, value]);
 
  const [position, setPosition] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (open && inputRef.current) {
      setPosition(inputRef.current.getBoundingClientRect());
    }
  }, [open, value]);
 
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open && e.key !== "Tab") setOpen(true);

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((i) =>
          Math.min(i + 1, filtered.length - 1)
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case "Enter":
        if (activeIndex >= 0 && filtered[activeIndex]) {
          e.preventDefault();
          setValue(filtered[activeIndex]);
          setOpen(false);
        }
        break;
      case "Escape":
        setOpen(false);
        break;
    }
  };
 
  const validate = (val: string) => {
    for (const rule of config.validation) {
      if (rule.tipo === "list") {
        if (!rule.permissions_list?.includes(val)) {
          return "Valor no permitido";
        }
      }
    }
    return null;
  };

  const handleSubmit = () => {
    const err = validate(value ?? "");
    setError(err);
    if (!err) onSubmit(value ?? "");
  };

  return (
    <div className="space-y-3">
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <label className="block font-medium">{config.label}</label>

        <InputChat
          actionCode={Action.LOGIN_ANONYMOUS_IDENTIFIER} 
          ref={inputRef}
          type={inputType}
          value={value ?? ""}
          {...inputAttrs}
          className="w-full border p-2 n-fmr-input focus:outline-none focus:ring-1 focus:ring-blue-700"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          placeholder={config.placeholder}
          onChange={(e) => {
            setValue(e.target.value ?? "");
            setOpen(true);
            setActiveIndex(-1);
          }}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            setTimeout(() => setOpen(false), 150);
            setError(validate(value ?? ""));
          }}
        />

        {open &&
          filtered.length > 0 &&
          position &&
          createPortal(
            <ul
              role="listbox"
              id={listboxId}
              className="fixed z-[9999] bg-white border rounded-md shadow-lg max-h-56 overflow-auto"
              style={{
                top: position.bottom + 4,
                left: position.left,
                width: position.width,
              }}
            >
              {filtered.map((item, index) => (
                <li
                  key={item}
                  role="option"
                  aria-selected={index === activeIndex}
                  className={`px-3 py-2 cursor-pointer ${
                    index === activeIndex
                      ? "bg-blue-600 text-white"
                      : "hover:bg-blue-100"
                  }`}
                  onMouseDown={() => {
                    setValue(item);
                    setOpen(false);
                  }}
                >
                  {item}
                </li>
              ))}
            </ul>,
            document.body
          )}

        {error && <p className="text-red-500 text-sm">{error}</p>}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 mb-2 hover:bg-blue-50 rounded-lg shadow-lg flex items-center justify-center n-buttons btn-login-border"
        >
          {loading ? "Validando..." : "Iniciar"}
        </button>
      </form>
    </div>
  );
}
